import { Fragment, useEffect, useRef, useState } from 'react';
import { CircleCheckIcon, CircleDashedIcon, CircleDotIcon, PencilIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ALGEMENE_VERWERKINGSFOUT,
  type MeetingSamenvatting,
  type MeetingStatus,
} from '@vip/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useHernoemGesprek } from './gesprekken.api';
import {
  IS_BEZIG,
  STAPPEN,
  STATUS_TEKST,
  WACHTTEKST,
  WACHT_OP_BUDDY,
  actie,
  stand,
  wanneer,
  weergave,
  type Stand,
} from './gesprekstand';

const ICOON: Record<Exclude<Stand, 'bezig'>, typeof CircleCheckIcon> = {
  klaar: CircleCheckIcon,
  jij: CircleDotIcon,
  open: CircleDashedIcon,
};

/** De vier stappen als één spoor: gedane stappen afgevinkt in de merkkleur, de
 *  stap waar het nu op staat zwaarder, wat nog komt gestippeld.
 *
 *  De labels gaan in `sr-only` zodra de kaart smal is. Vier woorden naast
 *  elkaar passen daar niet, maar ze horen wel voorgelezen te worden. */
function Spoor({ status }: { status: MeetingStatus }) {
  return (
    <div className="flex items-center gap-2">
      {STAPPEN.map((label, i) => {
        const s = stand(status, i);
        const Icoon = s === 'bezig' ? Spinner : ICOON[s];
        return (
          <Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <Icoon
                className={cn(
                  'size-3.5 shrink-0',
                  s === 'open' ? 'text-muted-foreground/40' : 'text-primary',
                  s === 'jij' && 'rounded-full ring-3 ring-primary/20',
                )}
              />
              <span
                className={cn(
                  'text-xs whitespace-nowrap sr-only @lg/kaart:not-sr-only',
                  s === 'jij' || s === 'bezig'
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            </div>
            {i < STAPPEN.length - 1 && (
              <Separator className={cn('min-w-3 flex-1', s === 'klaar' && 'bg-primary/30')} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/** De naam van het gesprek, ter plekke te wijzigen: een typefout in een eigen
 *  lijst hoef je niet via een formulier recht te zetten. */
function Naam({ gesprek }: { gesprek: MeetingSamenvatting }) {
  const hernoem = useHernoemGesprek();
  const [bewerkt, setBewerkt] = useState(false);
  const [waarde, setWaarde] = useState(gesprek.titel ?? '');
  const veld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (bewerkt) veld.current?.select();
  }, [bewerkt]);

  function afronden() {
    setBewerkt(false);
    if (waarde.trim() !== (gesprek.titel ?? '')) {
      hernoem.mutate({ id: gesprek.id, titel: waarde.trim() });
    }
  }

  if (bewerkt) {
    return (
      <Input
        aria-label="Naam van dit gesprek"
        maxLength={120}
        onBlur={afronden}
        onChange={(e) => setWaarde(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') afronden();
          if (e.key === 'Escape') {
            setWaarde(gesprek.titel ?? '');
            setBewerkt(false);
          }
        }}
        ref={veld}
        value={waarde}
      />
    );
  }

  return (
    <>
      <span className={cn('truncate', !gesprek.titel && 'font-normal text-muted-foreground')}>
        {gesprek.titel ?? 'Naam toevoegen'}
      </span>
      <Button
        aria-label="Naam wijzigen"
        onClick={() => {
          setWaarde(gesprek.titel ?? '');
          setBewerkt(true);
        }}
        size="icon-xs"
        variant="ghost"
      >
        <PencilIcon />
      </Button>
    </>
  );
}

/**
 * Eén gesprek: bovenin wat het is, in het midden hoe ver het is, onderin wat je
 * ermee kunt.
 *
 * Een gesprek dat op jou wacht valt op, want in een lijst is "welke is van mij?"
 * de enige vraag die je hebt. Dat ging met een streep van vier pixels langs de
 * linkerrand, en dat is een decoratie die je erbij plakt.
 *
 * Nu gebeurt het met de rand die de kaart zelf al heeft. `Card` staat op
 * `ring-1 ring-foreground/10`, een haarlijn die je nauwelijks ziet; hier wordt
 * dat dezelfde lijn in de merkkleur en één stap dikker. De hele kaart licht dus
 * op in plaats van dat er iets tegenaan staat, en het is geen nieuw element maar
 * de bestaande omranding die luider wordt.
 *
 * De verwijderknop staat er altijd en ligt daarmee op elke kaart op dezelfde
 * plek; wat je verder met dit gesprek kunt doen schuift ernaast, niet ertussen.
 */
export function GesprekKaart({
  gesprek: g,
  onVerwijder,
}: {
  gesprek: MeetingSamenvatting;
  onVerwijder: () => void;
}) {
  const huidig = weergave(g);
  const knop = actie({ ...g, status: huidig });
  const status = STATUS_TEKST[huidig];
  const jijAanZet = WACHT_OP_BUDDY.includes(huidig);

  return (
    <Card
      className={cn(
        '@container/kaart',
        jijAanZet && 'ring-2 ring-primary/40',
        g.status === 'FAILED' && 'ring-2 ring-destructive/40',
      )}
    >
      <CardHeader>
        <CardTitle aria-level={2} className="flex min-w-0 items-center gap-1" role="heading">
          <Naam gesprek={g} />
        </CardTitle>
        <CardDescription>
          {wanneer(g)} · {g.deelnemers} {g.deelnemers === 1 ? 'deelnemer' : 'deelnemers'}
          {g.keywords > 0 && ` · ${g.keywords} hulpvragen`}
          {g.audioDeletedAt && ' · opname verwijderd'}
        </CardDescription>
        <CardAction>
          <Badge variant={status.variant}>
            {IS_BEZIG.includes(huidig) && <Spinner />}
            {status.label}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent>
        <Spoor status={huidig} />
      </CardContent>

      <CardFooter className="flex-wrap gap-3">
        {/* De reden komt van de server en is daar al leesbaar gemaakt. */}
        {g.status === 'FAILED' ? (
          <p className="text-sm text-destructive">{g.failureReason ?? ALGEMENE_VERWERKINGSFOUT}</p>
        ) : (
          WACHTTEKST[huidig] && (
            <p className="text-sm text-muted-foreground">{WACHTTEKST[huidig]}</p>
          )
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {knop && (
            <Button asChild size="sm" variant={jijAanZet ? 'default' : 'outline'}>
              <Link to={knop.pad}>{knop.tekst}</Link>
            </Button>
          )}
          <Button onClick={onVerwijder} size="sm" variant="destructive">
            Verwijderen
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
